import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Container,
  Paper
} from '@mui/material';
import {
  ArrowBack,
  Search
} from '@mui/icons-material';
import SearchComponent from '../components/SearchComponent';
import ProductCard from '../components/ProductCard';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { searchProducts } from '../api/apiClient';

const SearchResultsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const query = searchParams.get('q');
  const gender = searchParams.get('gender') || 'WOMAN'; // Пол по умолчанию WOMAN
  const limit = 36; // Количество товаров на странице
  
  // Устанавливаем заголовок страницы после определения query
  useDocumentTitle(query ? `Поиск: ${query}` : 'Поиск');
  
  // Обновляем заголовок при изменении поискового запроса
  useEffect(() => {
    if (query) {
      document.title = `Поиск: ${query} | ZARA в Россию через @bayer_antalya`;
      
      // Также обновляем Open Graph заголовок
      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) {
        ogTitle.setAttribute('content', `Поиск: ${query} | ZARA в Россию через @bayer_antalya`);
      }
      
      // Обновляем Twitter заголовок
      const twitterTitle = document.querySelector('meta[name="twitter:title"]');
      if (twitterTitle) {
        twitterTitle.setAttribute('content', `Поиск: ${query} | ZARA в Россию через @bayer_antalya`);
      }
    }
  }, [query]);

  // useEffect для отслеживания изменений в query или gender
  useEffect(() => {
    if (query) {
      // Сбрасываем состояние при новом поиске или смене пола
      setPage(1);
      setSearchResults([]);
      setHasNextPage(false);
      setError('');
      
      // Сразу загружаем результаты для нового пола
      fetchSearchResults();
    }
  }, [query, gender]);

  // useEffect для загрузки дополнительных страниц
  useEffect(() => {
    if (query && page > 1 && !loading) {
      fetchMoreResults();
    }
  }, [page]);



  const fetchSearchResults = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Формируем параметры запроса
      const params = {
        q: query,
        gender: gender,
        page: page,
        limit: limit
      };
      
      // Используем наш backend API для поиска
      const response = await searchProducts(params);
      
      if (response.data && response.data.results) {
        // Отслеживаем поиск в Яндекс.Метрике
        if (window.trackSearch) {
          window.trackSearch(query, response.data.results.length);
        }
        
        const results = response.data.results.map(item => {
          const content = item.content;
          const firstColor = content.detail?.colors?.[0];
          

          
          // Ищем основное изображение
          let mainImage = null;
          if (firstColor?.xmedia && firstColor.xmedia.length > 0) {
            mainImage = firstColor.xmedia[0].url;
          }
          
          return {
            id: item.id,
            name: content.name,
            price: content.price,
            futurePrice: content.futurePrice,
            priceInRub: content.price, // Цена уже конвертирована в рубли на сервере
            originalPrice: content.originalPrice, // Оригинальная цена в KZT
            priceCurrency: content.priceCurrency || 'RUB', // Валюта цены
            originalPriceCurrency: content.originalPriceCurrency || 'KZT', // Оригинальная валюта
            description: content.description, // Описание товара
            reference: content.reference,
            displayReference: content.detail?.displayReference,
            sectionName: content.sectionName,
            familyName: content.familyName,
            subfamilyName: content.subfamilyName,
            availability: content.availability,
            mainImage: mainImage,
            colors: content.detail?.colors || [],
            colorInfo: content.colorInfo
          };
        });
        
        setSearchResults(results);
        // Обновляем информацию о пагинации
        if (response.data.pagination) {
          setHasNextPage(response.data.pagination.hasNextPage);
        }
      } else {
        setSearchResults([]);
        setHasNextPage(false);
      }
      
    } catch (err) {
      console.error('❌ Ошибка поиска:', err);
      setError('Ошибка при выполнении поиска');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Функция для загрузки дополнительных результатов (ленивая подгрузка)
  const fetchMoreResults = async () => {
    if (loadingMore || !hasNextPage) return;
    
    try {
      setLoadingMore(true);
      
      const params = {
        q: query,
        gender: gender,
        page: page,
        limit: limit
      };
      
      const response = await searchProducts(params);
      
      if (response.data && response.data.results) {
        const newResults = response.data.results.map(item => {
          const content = item.content;
          const firstColor = content.detail?.colors?.[0];
          
          let mainImage = null;
          if (firstColor?.xmedia && firstColor.xmedia.length > 0) {
            mainImage = firstColor.xmedia[0].url;
          }
          
          return {
            id: item.id,
            name: content.name,
            price: content.price,
            futurePrice: content.futurePrice,
            priceInRub: content.price, // Цена уже конвертирована в рубли на сервере
            originalPrice: content.originalPrice, // Оригинальная цена в KZT
            priceCurrency: content.priceCurrency || 'RUB', // Валюта цены
            originalPriceCurrency: content.originalPriceCurrency || 'KZT', // Оригинальная валюта
            description: content.description, // Описание товара
            reference: content.reference,
            displayReference: content.detail?.displayReference,
            sectionName: content.sectionName,
            familyName: content.familyName,
            subfamilyName: content.subfamilyName,
            availability: content.availability,
            mainImage: mainImage,
            colors: content.detail?.colors || [],
            colorInfo: content.colorInfo
          };
        });
        
        // Добавляем новые результаты к существующим
        setSearchResults(prev => [...prev, ...newResults]);
        
        // Обновляем информацию о пагинации
        if (response.data.pagination) {
          setHasNextPage(response.data.pagination.hasNextPage);
        }
      }
      
    } catch (err) {
      console.error('❌ Ошибка загрузки дополнительных результатов:', err);
      setError('Ошибка при загрузке дополнительных результатов');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  const handleNewSearch = (newQuery, newGender = gender) => {
    if (newQuery !== query || newGender !== gender) {
      // Обновляем URL для нового поиска
      const encodedQuery = encodeURIComponent(newQuery);
      const encodedGender = encodeURIComponent(newGender);
      navigate(`/search?q=${encodedQuery}&gender=${encodedGender}`);
    }
  };

  // Функция для ленивой подгрузки при прокрутке
  const handleScroll = () => {
    if (loadingMore || !hasNextPage) return;
    
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    
    // Загружаем следующую страницу когда пользователь приближается к концу
    if (scrollTop + windowHeight >= documentHeight - 100) {
      setPage(prev => prev + 1);
    }
  };

  // Добавляем слушатель прокрутки
  useEffect(() => {
    if (hasNextPage && !loadingMore) {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [hasNextPage, loadingMore]);

  if (!query) {
    return (
      <Container maxWidth="lg" className="search-page-container">
        <Box sx={{ 
          textAlign: 'center', 
          py: { xs: 4, md: 8 },
          px: { xs: 2, md: 0 }
        }}>
          <Typography 
            variant="h4" 
            sx={{ 
              mb: { xs: 2, md: 3 },
              fontSize: { xs: '1.8rem', sm: '2rem', md: '2.125rem' },
              fontWeight: 300
            }}
          >
            Поиск товаров
          </Typography>
          <SearchComponent 
            variant="hero" 
            placeholder="Введите название товара..."
            onSearch={handleNewSearch}
          />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" className="search-page-container">
      {/* Заголовок и поиск */}
      <Box sx={{ mb: { xs: 3, md: 4 } }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => window.history.back()}
          sx={{ 
            mb: { xs: 1.5, md: 2 }, 
            textTransform: 'none',
            fontSize: { xs: '0.9rem', md: '1rem' }
          }}
        >
          Назад
        </Button>
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' }, 
          gap: { xs: 1, sm: 2 }, 
          mb: { xs: 2, md: 3 } 
        }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 300,
              fontSize: { xs: '1.6rem', sm: '1.8rem', md: '2.125rem' }
            }}
          >
            Результаты поиска: "{query}"
          </Typography>
          <Chip 
            label={gender === 'WOMAN' ? 'Женщины' : gender === 'MAN' ? 'Мужчины' : 'Дети'}
            color="primary"
            variant="outlined"
            size="small"
            sx={{ 
              fontSize: { xs: '0.75rem', md: '0.875rem' },
              height: { xs: 24, md: 32 }
            }}
          />
        </Box>
        
        <SearchComponent 
          variant="compact" 
          placeholder={`Поиск: ${query}`}
          onSearch={handleNewSearch}
          initialGender={gender}
        />
      </Box>



      {/* Результаты поиска */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: { xs: 6, md: 8 } }}>
          <CircularProgress size={60} />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: { xs: 3, md: 4 } }}>
          {error}
        </Alert>
      ) : searchResults.length === 0 ? (
        <Paper sx={{ 
          p: { xs: 3, md: 6 }, 
          textAlign: 'center',
          mx: { xs: 1, md: 0 }
        }}>
          <Search sx={{ 
            fontSize: { xs: 60, md: 80 }, 
            color: '#ddd', 
            mb: { xs: 1.5, md: 2 } 
          }} />
          <Typography 
            variant="h6" 
            sx={{ 
              mb: { xs: 1.5, md: 2 },
              fontSize: { xs: '1.1rem', md: '1.25rem' }
            }}
          >
            По вашему запросу ничего не найдено
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ 
              mb: { xs: 2, md: 3 },
              fontSize: { xs: '0.875rem', md: '1rem' }
            }}
          >
            Попробуйте изменить поисковый запрос или использовать другие ключевые слова
          </Typography>
          <SearchComponent 
            variant="compact" 
            placeholder="Новый поиск..."
            onSearch={handleNewSearch}
          />
        </Paper>
      ) : (
        <>
     
          {/* Сетка товаров */}
          <Box className="product-grid" sx={{ 
            mb: { xs: 3, md: 4 },
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(4, 1fr)',
              lg: 'repeat(5, 1fr)'
            },
            gap: { xs: 1, sm: 2, md: 3 }
          }}>
            {searchResults.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onClick={() => handleProductClick(product.id)}
                showColors={true}
                showFavorite={false}
                variant="default"
              />
            ))}
          </Box>

          {/* Индикатор загрузки дополнительных результатов */}
          {loadingMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: { xs: 3, md: 4 } }}>
              <CircularProgress size={40} />
              <Typography 
                variant="body2" 
                sx={{ 
                  ml: 2, 
                  alignSelf: 'center',
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }}
              >
                Загружаем еще товары...
              </Typography>
            </Box>
          )}

          {/* Кнопка "Загрузить еще" для ручной загрузки */}
          {hasNextPage && !loadingMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 3, md: 4 } }}>
              <Button
                variant="contained"
                onClick={() => setPage(prev => prev + 1)}
                sx={{ 
                  textTransform: 'none',
                  fontSize: { xs: '0.875rem', md: '1rem' },
                  py: { xs: 1.5, md: 2 },
                  px: { xs: 3, md: 4 },
                  borderRadius: 2
                }}
              >
                Загрузить еще товары
              </Button>
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default SearchResultsPage;
